import { niceStep } from './cartesianChart'

/**
 * Y-axis tick generation shared by the horizontally-gridded chart primitives
 * (TimeSeriesChart, ComposedChart, ColumnChart). Handing recharts an explicit set
 * of evenly-spaced "nice" ticks — with the domain snapped to the first/last tick —
 * pins the horizontal grid lines to an exact 48px pixel cadence and gives round
 * labels (0, 500, 1k, 1.5k, 2k). Left to `tickCount` alone, recharts places ticks
 * by value inside a padded domain, so neither the spacing nor the labels are fixed.
 * Pure, no React. @see ./xAxisTicks for the x-axis sibling.
 */

export interface YTicks {
  /** Evenly-spaced tick values, length `count`, enclosing the data range. */
  ticks: number[]
  /** Domain snapped to `[firstTick, lastTick]` so ticks span the whole plot. */
  domain: [number, number]
}

/** Next rung of the 1 / 2 / 5 × 10ⁿ ladder above `step` (×1.5 always crosses to
 *  the next rung); the `* 2` guard is a defensive floor that never triggers. */
function advanceStep(step: number, integer: boolean): number {
  const bumped = niceStep(step * 1.5, integer)
  return bumped > step ? bumped : step * 2
}

/**
 * `count` evenly-spaced nice ticks enclosing `[min, max]`, plus the domain snapped
 * to their extremes. `zeroBaseline` anchors the lowest tick at 0 (columns/bars);
 * otherwise it floors to the nearest step below `min` so a line fills the plot.
 * `integer` keeps the step whole for count axes (percentage axes allow fractions).
 *
 * Degenerate ranges (`max <= min`, or non-finite bounds — the no-data / all-zero
 * placeholder domains) fall back to `count` unit ticks from 0, so the grid still
 * renders `count` lines at the fixed cadence.
 */
export function computeNiceYTicks(
  min: number,
  max: number,
  count: number,
  opts: { integer: boolean; zeroBaseline: boolean },
): YTicks {
  const gaps = Math.max(1, count - 1)

  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    const ticks = Array.from({ length: count }, (_, i) => i)
    return { ticks, domain: [0, count - 1] }
  }

  let step = niceStep((max - min) / gaps, opts.integer)
  let niceMin = opts.zeroBaseline ? 0 : Math.floor(min / step) * step
  // Flooring the baseline down can leave the top tick below `max`; grow the step
  // one rung at a time until `count` gaps span the data. Terminates because `min`
  // here is always ≥ 0 (see computeYDomain), so a growing step drives niceMin → 0.
  while (niceMin + step * gaps < max) {
    step = advanceStep(step, opts.integer)
    niceMin = opts.zeroBaseline ? 0 : Math.floor(min / step) * step
  }

  const ticks = Array.from({ length: count }, (_, i) => niceMin + i * step)
  return { ticks, domain: [ticks[0], ticks[ticks.length - 1]] }
}

/**
 * {@link computeNiceYTicks} over a {@link computeYDomain} result, whose bounds may
 * be `'auto'` (the all-zero / no-data placeholder) — those route to the degenerate
 * unit-tick fallback so the grid still renders `count` lines at the fixed cadence.
 */
export function yTicksForDomain(
  domain: [number, number | string],
  count: number,
  opts: { integer: boolean; zeroBaseline: boolean },
): YTicks {
  const [min, max] = domain
  return typeof min === 'number' && typeof max === 'number'
    ? computeNiceYTicks(min, max, count, opts)
    : computeNiceYTicks(0, Number.NaN, count, opts)
}
